import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  TextField,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Groups as GroupsIcon,
  ArrowBack as BackIcon,
  SwapHoriz as SwapHorizIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  GroupAdd as GroupAddIcon,
  RemoveCircle as RemoveCircleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useQueryClient } from 'react-query';
import { Player, playerService } from '@/services/playerService';
import { leagueService, League } from '@/services/leagueService';
import { teamService, Team } from '@/services/teamService';
import { TeamLogo } from '@/components/TeamLogo';
import LeagueCatalogDialog from '@/components/LeagueCatalogDialog';
import { syncSchedulerService } from '@/services/syncSchedulerService';

interface PlayerTeamsTabProps {
  player: Player;
  onUpdate?: () => void;
  canModify: boolean;
  wizardMode?: boolean;
  onDone?: (teamAssignments?: any[]) => void;
  onBack?: () => void;
  wizardTeamAssignments?: any[];
  onTeamAssignmentsChange?: (assignments: any[]) => void;
}

const NEW_LEAGUE_SENTINEL: League = { id: '__new__', name: 'New League' } as League;

interface AddTeamFormState {
  selectedLeague: League | null;
  selectedTeam: Team | null;
}

interface ChangeTeamDialogState {
  open: boolean;
  assignmentId: string | null;
  currentLeagueId: string | null;
  currentLeagueName: string | null;
  currentTeamName: string | null;
  selectedTeam: Team | null;
}

const PlayerTeamsTab: React.FC<PlayerTeamsTabProps> = ({
  player,
  onUpdate,
  canModify,
  wizardMode = false,
  onDone,
  onBack,
  wizardTeamAssignments = [],
  onTeamAssignmentsChange
}) => {
  const queryClient = useQueryClient();
  
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [showCatalogDialog, setShowCatalogDialog] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  const [changeTeamDialog, setChangeTeamDialog] = useState<ChangeTeamDialogState>({
    open: false,
    assignmentId: null,
    currentLeagueId: null,
    currentLeagueName: null,
    currentTeamName: null,
    selectedTeam: null
  });
  
  const [leagues, setLeagues] = useState<League[]>([]);
  const [leaguesLoaded, setLeaguesLoaded] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [leaguesLoading, setLeaguesLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  
  const [addTeamForm, setAddTeamForm] = useState<AddTeamFormState>({
    selectedLeague: null,
    selectedTeam: null
  });

  // Load leagues when add team dialog opens
  useEffect(() => {
    if (showAddTeam) {
      loadLeagues();
    }
  }, [showAddTeam]);

  // Load teams when league is selected
  useEffect(() => {
    if (addTeamForm.selectedLeague) {
      loadTeams(addTeamForm.selectedLeague.id);
    } else {
      setTeams([]);
    }
  }, [addTeamForm.selectedLeague]);

  // Load teams when change team dialog opens
  useEffect(() => {
    if (changeTeamDialog.open && changeTeamDialog.currentLeagueId) {
      loadTeams(changeTeamDialog.currentLeagueId);
    }
  }, [changeTeamDialog.open, changeTeamDialog.currentLeagueId]);

  const loadLeagues = async () => {
    try {
      setLeaguesLoading(true);
      // Fetch all leagues (use high limit since this is for a dropdown)
      const leaguesData = await leagueService.getLeagues(1, 1000);
      const loaded = leaguesData.data || leaguesData;
      setLeagues(loaded);
      setLeaguesLoaded(true);
      return loaded as League[];
    } catch (error) {
      console.error('Error loading leagues:', error);
      toast.error('Failed to load leagues');
      return [];
    } finally {
      setLeaguesLoading(false);
    }
  };

  const loadTeams = async (leagueId: string) => {
    try {
      setTeamsLoading(true);
      const teamsData = await teamService.getTeamsByLeague(leagueId);
      setTeams(teamsData);
    } catch (error) {
      console.error('Error loading teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setTeamsLoading(false);
    }
  };

  const handleAddTeam = async () => {
    if (!addTeamForm.selectedLeague || !addTeamForm.selectedTeam) {
      return;
    }

    const newAssignment = {
      leagueId: addTeamForm.selectedLeague.id,
      teamName: addTeamForm.selectedTeam.name,
      clubId: addTeamForm.selectedTeam.clubId,
      division: addTeamForm.selectedTeam.division,
      season: addTeamForm.selectedTeam.season,
      jerseyNumber: null,
      position: null,
      isStarter: false,
      notes: '',
      // Include league object for display purposes in wizard mode
      league: addTeamForm.selectedLeague,
      // Include team logo information for display
      teamId: addTeamForm.selectedTeam.id,
      teamLogoPath: addTeamForm.selectedTeam.logoPath,
      teamLogoUrl: addTeamForm.selectedTeam.logoUrl
    };

    if (wizardMode) {
      // In wizard mode, just add to local state
      const isDuplicate = wizardTeamAssignments.some(assignment => 
        assignment.leagueId === newAssignment.leagueId && 
        assignment.teamName === newAssignment.teamName
      );

      if (isDuplicate) {
        toast.error(`${newAssignment.teamName} is already assigned to this player`);
        return;
      }

      onTeamAssignmentsChange?.([...wizardTeamAssignments, newAssignment]);
      toast.success('Team assignment added');
      setShowAddTeam(false);
      resetAddTeamForm();
      return;
    }

    // Regular mode - save to database
    const isDuplicate = player.leagueAssignments?.some(assignment => 
      assignment.league.id === addTeamForm.selectedLeague!.id && 
      assignment.teamName === addTeamForm.selectedTeam!.name
    );

    if (isDuplicate) {
      toast.error(`${addTeamForm.selectedTeam.name} is already assigned to this player`);
      return;
    }

    try {
      setLoading(true);

      // Clean assignment data for API - remove league object
      const cleanAssignment = {
        leagueId: newAssignment.leagueId,
        teamName: newAssignment.teamName,
        clubId: newAssignment.clubId,
        division: newAssignment.division,
        season: newAssignment.season,
        jerseyNumber: newAssignment.jerseyNumber,
        position: newAssignment.position,
        isStarter: newAssignment.isStarter,
        notes: newAssignment.notes
      };

      console.log('Adding team assignment:', cleanAssignment);
      await playerService.assignToLeague(player.id, cleanAssignment);

      // Invalidate schedule and conflicts to trigger refetch for active observers
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && (
            key[0] === 'schedule' ||
            key[0] === 'conflicts'
          );
        }
      });

      toast.success('Team assignment added successfully');
      setShowAddTeam(false);
      resetAddTeamForm();
      onUpdate?.();
    } catch (error: any) {
      console.error('Error adding team assignment:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to add team assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTeam = (assignmentId: string) => {
    if (wizardMode) {
      // In wizard mode, remove from local state
      const updatedAssignments = wizardTeamAssignments.filter(assignment => 
        `${assignment.leagueId}-${assignment.teamName}` !== assignmentId
      );
      onTeamAssignmentsChange?.(updatedAssignments);
      toast.success('Team assignment removed');
      return;
    }

    // Regular mode
    setAssignmentToDelete(assignmentId);
    setDeleteConfirmOpen(true);
  };

  const handleChangeTeam = (assignment: any) => {
    setChangeTeamDialog({
      open: true,
      assignmentId: assignment.id,
      currentLeagueId: assignment.league.id,
      currentLeagueName: assignment.league.division ? `${assignment.league.name} - ${assignment.league.division}` : assignment.league.name,
      currentTeamName: assignment.teamName,
      selectedTeam: null
    });
  };

  const handleSubmitTeamChange = async () => {
    if (!changeTeamDialog.selectedTeam || !changeTeamDialog.assignmentId) {
      return;
    }
    
    // Prevent changing to the same team
    if (changeTeamDialog.selectedTeam.name === changeTeamDialog.currentTeamName) {
      toast.error('Player is already assigned to this team');
      return;
    }
    
    try {
      setLoading(true);
      
      await playerService.changeTeam(
        player.id,
        changeTeamDialog.assignmentId,
        {
          teamId: changeTeamDialog.selectedTeam.id,
          teamName: changeTeamDialog.selectedTeam.name
        }
      );
      
      // Clear caches to refresh data
      queryClient.invalidateQueries({ queryKey: ['player', player.id] });

      // Invalidate schedule and conflicts to trigger refetch for active observers
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && (
            key[0] === 'schedule' ||
            key[0] === 'conflicts'
          );
        }
      });

      toast.success(`Team changed to ${changeTeamDialog.selectedTeam.name}`);
      setChangeTeamDialog({ ...changeTeamDialog, open: false });
    } catch (error: any) {
      console.error('Error changing team:', error);
      
      // Handle specific error scenarios
      const errorMessage = error.response?.data?.error?.message || error.message;
      const errorCode = error.response?.data?.error?.code;
      
      if (errorCode === 'VALIDATION_ERROR') {
        if (errorMessage.includes('already assigned to this team')) {
          toast.error('Player is already assigned to this team');
        } else if (errorMessage.includes('does not belong to assignment league')) {
          toast.error('Selected team does not belong to this league');
        } else {
          toast.error(errorMessage || 'Invalid team selection');
        }
      } else if (errorCode === 'FORBIDDEN') {
        toast.error('You do not have permission to change this team assignment');
      } else if (errorCode === 'NOT_FOUND') {
        if (errorMessage.includes('Player not found')) {
          toast.error('Player not found');
        } else if (errorMessage.includes('Assignment not found')) {
          toast.error('Team assignment not found');
        } else {
          toast.error('The requested resource was not found');
        }
      } else if (error.response?.status === 500) {
        toast.error('Server error occurred. Please try again.');
      } else if (!error.response) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error(errorMessage || 'Failed to change team');
      }
      
      // Keep dialog open on error to allow retry
    } finally {
      setLoading(false);
    }
  };

  const confirmRemoveTeam = async () => {
    if (!assignmentToDelete) return;

    try {
      setLoading(true);
      await playerService.removeFromLeague(player.id, assignmentToDelete);

      // Invalidate schedule and conflicts to trigger refetch for active observers
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && (
            key[0] === 'schedule' ||
            key[0] === 'conflicts'
          );
        }
      });

      toast.success('Team assignment removed successfully');
      setDeleteConfirmOpen(false);
      setAssignmentToDelete(null);
      onUpdate?.();
    } catch (error: any) {
      console.error('Error removing team assignment:', error);
      toast.error('Failed to remove team assignment');
    } finally {
      setLoading(false);
    }
  };

  const resetAddTeamForm = () => {
    setAddTeamForm({
      selectedLeague: null,
      selectedTeam: null
    });
  };

  const handleLeagueImported = async (league: any, opts?: { teamName?: string; playerId?: string }) => {
    // Background sync for schedule import
    try {
      await syncSchedulerService.triggerManualSync(league.id);
    } catch (err) {
      console.error('Failed to start initial sync:', err);
    }
    queryClient.invalidateQueries(['leagues']);

    if (opts?.teamName) {
      // Auto-assign player to the selected team
      const assignData = {
        leagueId: league.id,
        teamName: opts.teamName,
      };

      if (wizardMode) {
        // In wizard mode, add to local state
        const isDuplicate = wizardTeamAssignments.some(a =>
          a.leagueId === league.id && a.teamName === opts.teamName
        );
        if (!isDuplicate) {
          onTeamAssignmentsChange?.([...wizardTeamAssignments, {
            ...assignData,
            league,
            jerseyNumber: null,
            position: null,
            isStarter: false,
            notes: '',
          }]);
        }
        setShowCatalogDialog(false);
        setShowAddTeam(false);
        resetAddTeamForm();
        toast.success(`League imported and player assigned to ${opts.teamName}`);
        return;
      }

      // Regular mode - save to database
      try {
        setLoading(true);
        await playerService.assignToLeague(player.id, assignData);
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && (key[0] === 'schedule' || key[0] === 'conflicts');
          }
        });
        setShowCatalogDialog(false);
        setShowAddTeam(false);
        resetAddTeamForm();
        toast.success(`League imported and player assigned to ${opts.teamName}`);
        onUpdate?.();
      } catch (err) {
        console.error('Failed to assign player to team:', err);
        toast.error('League imported but failed to assign player. You can assign manually.');
        // Fall through to normal behavior
        setShowCatalogDialog(false);
        setShowAddTeam(true);
        setLeagues(prev => [league, ...prev]);
        setAddTeamForm(prev => ({ ...prev, selectedLeague: league, selectedTeam: null }));
      } finally {
        setLoading(false);
      }
      return;
    }

    // No team selected - existing behavior
    setShowCatalogDialog(false);
    setShowAddTeam(true);
    setLeagues(prev => [league, ...prev]);
    setAddTeamForm(prev => ({
      ...prev,
      selectedLeague: league,
      selectedTeam: null,
    }));
    toast.success(`League "${league.name}" imported! Select a team below.`);
  };

  const handleOpenAddTeam = async () => {
    if (leaguesLoaded && leagues.length === 0) {
      // No leagues in org — go straight to catalog
      setShowCatalogDialog(true);
      return;
    }
    if (!leaguesLoaded) {
      // First time — fetch leagues and check
      const loaded = await loadLeagues();
      if (loaded.length === 0) {
        setShowCatalogDialog(true);
        return;
      }
    }
    setShowAddTeam(true);
  };

  const handleCloseAddTeam = () => {
    setShowAddTeam(false);
    resetAddTeamForm();
  };

  const activeAssignments = wizardMode 
    ? wizardTeamAssignments 
    : (player.leagueAssignments?.filter(assignment => assignment.active) || []);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        {/* Mobile heading */}
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: { xs: 'block', md: 'none' } }}>
          {wizardMode ? 'Add Teams (Optional)' : `${player.name}'s Teams`}
        </Typography>
        {/* Desktop heading */}
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: { xs: 'none', md: 'block' } }}>
          {wizardMode ? 'Add Teams (Optional)' : 'Team Assignments'}
        </Typography>
      </Box>

      {/* Wizard mode message */}
      {wizardMode && (
        <Card sx={{ mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <CardContent>
            <Typography variant="body2">
              If you don't see your player's team, add their league and then assign your player to a team in the league.
            </Typography>
          </CardContent>
        </Card>
      )}

      {activeAssignments.length === 0 ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box 
              sx={{ 
                textAlign: 'center',
                py: 6,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'grey.300'
              }}
            >
              <GroupsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No team assignments
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This player is not currently assigned to any teams
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {activeAssignments.map((assignment) => {
            const assignmentId = wizardMode 
              ? `${assignment.leagueId}-${assignment.teamName}` 
              : assignment.id;
            
            return (
              <Card key={assignmentId} sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <TeamLogo 
                      teamId={assignment.teamId || null} 
                      teamName={assignment.teamName} 
                      size={32} 
                      logoPath={assignment.teamLogoPath}
                      logoUrl={assignment.teamLogoUrl}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" color="primary.dark" sx={{ fontWeight: 500 }}>
                        {assignment.teamName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {assignment.league?.division ? `${assignment.league.name} - ${assignment.league.division}` : (assignment.league?.name || 'Unknown League')}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {(assignment.position || assignment.jerseyNumber || assignment.division) && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      {assignment.position && (
                        <Chip
                          label={`Position: ${assignment.position}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      {assignment.jerseyNumber && (
                        <Chip
                          label={`Jersey: ${assignment.jerseyNumber}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      {assignment.division && (
                        <Chip
                          label={`Division: ${assignment.division}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                    </Box>
                  )}

                  {canModify && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, mt: 2 }}>
                      <Button
                        size="small"
                        startIcon={<SwapHorizIcon />}
                        variant="outlined"
                        onClick={() => handleChangeTeam(assignment)}
                        disabled={loading || wizardMode}
                        sx={{ 
                          fontSize: '11px', 
                          justifyContent: 'center',
                          background: 'white',
                          color: 'text.primary',
                          borderColor: 'divider',
                          '&:hover': { borderColor: 'divider', backgroundColor: 'white' }
                        }}
                        aria-label={`Change team for ${assignment.teamName}`}
                      >
                        Change Team
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        variant="outlined"
                        onClick={() => handleRemoveTeam(assignmentId)}
                        disabled={loading}
                        sx={{ 
                          fontSize: '11px', 
                          justifyContent: 'center',
                          background: 'white',
                          color: 'text.primary',
                          borderColor: 'divider',
                          '&:hover': { borderColor: 'divider', backgroundColor: 'white' }
                        }}
                        aria-label={`Remove ${assignment.teamName} team assignment`}
                      >
                        Remove
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Add Team Dialog */}
      <Dialog
        open={showAddTeam}
        onClose={handleCloseAddTeam}
        maxWidth="sm"
        fullWidth
        transitionDuration={0}
        disableAutoFocus
        aria-labelledby="add-team-dialog-title"
        aria-describedby="add-team-dialog-description"
        sx={showCatalogDialog ? {
          '& .MuiDialog-paper': { visibility: 'hidden' },
        } : undefined}
      >
        <DialogTitle id="add-team-dialog-title"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><GroupAddIcon />Add Team Assignment</Box></DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} id="add-team-dialog-description">
            Select a league and team to assign this player to.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={[NEW_LEAGUE_SENTINEL, ...leagues]}
                getOptionLabel={(option) => {
                  if (option.id === '__new__') return 'New League';
                  return option.division ? `${option.name} - ${option.division}` : option.name;
                }}
                value={addTeamForm.selectedLeague}
                onChange={(_, value) => {
                  if (value?.id === '__new__') {
                    setShowCatalogDialog(true);
                    return;
                  }
                  setAddTeamForm(prev => ({
                    ...prev,
                    selectedLeague: value,
                    selectedTeam: null
                  }));
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={leaguesLoading}
                filterOptions={(options, state) => {
                  const filtered = options.filter(option => {
                    if (option.id === '__new__') return true; // Always show
                    const label = option.division ? `${option.name} - ${option.division}` : option.name;
                    return label.toLowerCase().includes(state.inputValue.toLowerCase());
                  });
                  return filtered;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option.id === '__new__' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', minHeight: 48 }}>
                        <AddIcon fontSize="small" />
                        <Typography variant="body1" fontWeight={500}>Import from catalog</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ minWidth: 0, width: '100%' }}>
                        <Typography variant="body1">{option.name}</Typography>
                        {(option.description || option.division || !option.isGlobalLeague) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {option.description || option.division || (!option.isGlobalLeague ? 'Custom' : '')}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Leagues"
                    placeholder="Type to search leagues"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {leaguesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={teams}
                getOptionLabel={(option) => `${option.name}${option.division ? ` (${option.division})` : ''}`}
                value={addTeamForm.selectedTeam}
                onChange={(_, value) => setAddTeamForm(prev => ({ ...prev, selectedTeam: value }))}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!addTeamForm.selectedLeague}
                loading={teamsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Team"
                    placeholder="Choose a team"
                    disabled={!addTeamForm.selectedLeague}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {teamsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseAddTeam} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddTeam}
            disabled={!addTeamForm.selectedLeague || !addTeamForm.selectedTeam || loading}
            startIcon={loading ? undefined : <AddIcon />}
          >
            {loading ? 'Adding...' : 'Add Team'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-confirm-dialog-title"
        aria-describedby="delete-confirm-dialog-description"
      >
        <DialogTitle id="delete-confirm-dialog-title"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><RemoveCircleIcon />Remove Team Assignment</Box></DialogTitle>
        <DialogContent>
          <Typography id="delete-confirm-dialog-description">
            Are you sure you want to remove this team assignment? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmRemoveTeam}
            disabled={loading}
            startIcon={<RemoveCircleIcon />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Team Dialog */}
      <Dialog
        open={changeTeamDialog.open}
        onClose={() => setChangeTeamDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
        disableAutoFocus
        aria-labelledby="change-team-dialog-title"
        aria-describedby="change-team-dialog-description"
      >
        <DialogTitle id="change-team-dialog-title"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SwapHorizIcon />Change Team</Box></DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} id="change-team-dialog-description">
            Change {player.name}'s team assignment in {changeTeamDialog.currentLeagueName}.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="League"
                value={changeTeamDialog.currentLeagueName || ''}
                disabled
                fullWidth
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={[
                  // Current team at top, disabled
                  teams.find(t => t.name === changeTeamDialog.currentTeamName),
                  // Other teams sorted alphabetically
                  ...teams
                    .filter(t => t.name !== changeTeamDialog.currentTeamName)
                    .sort((a, b) => a.name.localeCompare(b.name))
                ].filter(Boolean) as Team[]}
                getOptionLabel={(option) => option.name}
                getOptionDisabled={(option) => option.name === changeTeamDialog.currentTeamName}
                value={changeTeamDialog.selectedTeam}
                onChange={(_, value) => setChangeTeamDialog(prev => ({ ...prev, selectedTeam: value }))}
                loading={teamsLoading}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      {option.name === changeTeamDialog.currentTeamName && (
                        <Typography variant="caption" color="text.secondary">
                          Current team
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select New Team"
                    placeholder="Choose a team"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {teamsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setChangeTeamDialog(prev => ({ ...prev, open: false }))} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitTeamChange}
            disabled={!changeTeamDialog.selectedTeam || loading}
            startIcon={loading ? undefined : <SwapHorizIcon />}
          >
            {loading ? 'Changing...' : 'Change'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* League Catalog Dialog */}
      <LeagueCatalogDialog
        open={showCatalogDialog}
        onClose={() => {
          setShowCatalogDialog(false);
          setShowAddTeam(false);
          resetAddTeamForm();
        }}
        onBack={() => {
          setShowCatalogDialog(false);
        }}
        onImport={handleLeagueImported}
        teamPickerMode
        selectedPlayerId={player.id}
        selectedPlayerName={player.name}
      />

      {/* Action Buttons */}
      {(wizardMode || onDone) && (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
          <Box>
            {wizardMode && onBack && (
              <Button
                variant="outlined"
                onClick={onBack}
                startIcon={<BackIcon />}
                aria-label="Go back to player information"
              >
                Back
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {canModify && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenAddTeam}
                aria-label="Add new team assignment"
              >
                Add Team
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={() => onDone?.(wizardMode ? wizardTeamAssignments : undefined)}
              aria-label={wizardMode ? "Complete player creation" : "Return to players page"}
            >
              Done
            </Button>
          </Box>
        </Box>
      )}

      {/* Mobile FAB */}
      {canModify && (
        <Fab
          color="primary"
          aria-label="Add team assignment"
          onClick={handleOpenAddTeam}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            display: { xs: 'flex', md: 'none' }
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default PlayerTeamsTab;
